/**
 * MedicalDisclaimerModal
 *
 * Shared bottom-sheet modal presenting the Eleplan medical disclaimer.
 *
 * Required by the Google Play "Health Content and Services" policy: users
 * must be able to see, in plain English, that Eleplan is not a medical
 * device and does not diagnose, treat, cure, or prevent any condition.
 *
 * Surfaced in three places so reviewers (and users) don't have to hunt:
 *  - Settings → Legal → Medical Disclaimer
 *  - Onboarding "Your health data deserves protection" slide footer
 *  - AccountChat composer footer ("Not medical advice. Read disclaimer")
 *
 * All three entry points open this same modal. The copy is deliberately
 * plain and covers: not a medical device, organizational tool only, not
 * medical advice, consult a professional, emergency fallback.
 */

import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  ScrollView,
  TouchableOpacity,
} from 'react-native';
import { useTheme } from '../hooks/useTheme';
import { useSheetLayout } from '../utilities/sheetLayout';

interface Props {
  visible: boolean;
  onClose: () => void;
}

export default function MedicalDisclaimerModal({ visible, onClose }: Props) {
  const theme = useTheme();
  const sheet = useSheetLayout({ desktopMaxWidth: 560, desktopMaxHeightRatio: 0.9 });

  return (
    <Modal
      visible={visible}
      transparent
      animationType={sheet.animationType}
      onRequestClose={onClose}
    >
      <View style={[styles.overlay, sheet.overlayStyle]}>
        <View style={[styles.sheet, { backgroundColor: theme.colors.card }, sheet.sheetStyle, sheet.desktopContentStyle]}>
          {sheet.showHandle && <View style={[styles.handle, { backgroundColor: theme.colors.border }]} />}
          <Text style={[styles.title, { color: theme.colors.text }]}>
            Medical Disclaimer
          </Text>
          <ScrollView
            style={styles.scroll}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator
          >
            <Text style={[styles.lead, { color: theme.colors.text }]}>
              Eleplan is not a medical device and does not diagnose, treat, cure, or
              prevent any disease or medical condition.
            </Text>
            <Text style={[styles.body, { color: theme.colors.muted }]}>
              Eleplan is a care coordination and organizational tool. Information you
              enter, store, or view in the app — including medications, documents,
              contacts, calendar entries, surveys, messages, and any data surfaced
              from Apple Health or Google Health Connect — is provided for
              organizational and informational purposes only. It is not medical advice
              and is not a substitute for professional medical care, diagnosis, or
              treatment.
            </Text>
            <Text style={[styles.body, { color: theme.colors.muted }]}>
              Reminders, prompts, and notifications in Eleplan are intended to help
              you stay organized. They are not clinical alerts and should not be
              relied on to make medical decisions. Always consult a qualified
              healthcare provider for questions about a medical condition or
              treatment.
            </Text>
            <Text style={[styles.body, { color: theme.colors.muted }]}>
              AI-generated responses in Eleplan are informational only. They may be
              inaccurate or incomplete and must not be used as medical advice.
            </Text>
            <Text style={[styles.body, { color: theme.colors.muted }]}>
              Never disregard professional medical advice or delay seeking it because
              of something you have read or seen in Eleplan.
            </Text>
            <Text style={[styles.emergency, { color: theme.colors.error }]}>
              In an emergency, call your local emergency number (such as 911 in the
              United States) or go to the nearest emergency room. Do not use Eleplan
              to request emergency services.
            </Text>
          </ScrollView>
          <TouchableOpacity
            style={[styles.button, { backgroundColor: theme.colors.primary }]}
            onPress={onClose}
            activeOpacity={0.85}
          >
            <Text style={styles.buttonText}>I understand</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  sheet: {
    borderTopLeftRadius: 22,
    borderTopRightRadius: 22,
    padding: 24,
    paddingBottom: 40,
    maxHeight: '85%',
  },
  handle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    alignSelf: 'center',
    marginBottom: 20,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 12,
  },
  scroll: {
    marginBottom: 16,
  },
  scrollContent: {
    paddingBottom: 8,
  },
  lead: {
    fontSize: 15,
    lineHeight: 22,
    fontWeight: '600',
    marginBottom: 14,
  },
  body: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 12,
  },
  emergency: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: '600',
    marginBottom: 4,
  },
  button: {
    height: 50,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});
